import React, { useEffect, useState } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, X, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { useConfirm } from "@/components/ConfirmDialog";

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const CATEGORIES = ["Sub-12", "Sub-14", "Sub-16", "Sub-20", "Adulto", "Livre"];
const emptyScheduleRow = () => ({ day: "Seg", start: "18:00", end: "19:30" });
const emptyForm = { name: "", schedule: [emptyScheduleRow()], category: "Livre", description: "" };

// Build schedule from legacy fields if needed
function deriveSchedule(c) {
  if (Array.isArray(c?.schedule) && c.schedule.length) return c.schedule;
  if (Array.isArray(c?.days) && c?.time) {
    const [start, end] = String(c.time).split("-").map(s => s.trim());
    return c.days.map(d => ({ day: d, start: start || "", end: end || "" }));
  }
  return [];
}

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/classes"), api.get("/students")]);
      setClasses(c.data);
      setStudents(s.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const startEdit = (c) => {
    setEditing(c);
    const sched = deriveSchedule(c);
    setForm({
      name: c.name,
      schedule: sched.length ? sched : [emptyScheduleRow()],
      category: c.category,
      description: c.description || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.schedule.length) return toast.error("Adicione ao menos um horário");
    for (const s of form.schedule) {
      if (!s.day || !s.start || !s.end) return toast.error("Preencha dia, início e fim de todos os horários");
    }
    try {
      const payload = {
        name: form.name,
        category: form.category,
        description: form.description,
        schedule: form.schedule,
      };
      if (editing) { await api.put(`/classes/${editing.id}`, payload); toast.success("Turma atualizada"); }
      else { await api.post("/classes", payload); toast.success("Turma criada"); }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (c) => {
    const ok = await confirm({
      title: `Excluir turma ${c.name}?`,
      description: "Os alunos associados ficarão sem turma. A ação não pode ser desfeita.",
      confirmText: "Excluir", destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/classes/${c.id}`); toast.success("Turma excluída"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const addRow = () => setForm(f => ({ ...f, schedule: [...f.schedule, emptyScheduleRow()] }));
  const removeRow = (idx) => setForm(f => ({ ...f, schedule: f.schedule.filter((_, i) => i !== idx) }));
  const updateRow = (idx, patch) => setForm(f => ({
    ...f,
    schedule: f.schedule.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));

  const countStudents = (cid) => students.filter(s => s.class_id === cid).length;

  return (
    <div data-testid="admin-classes-page">
      <PageHeader
        eyebrow="Organização"
        title="Turmas"
        subtitle="Crie e gerencie as turmas com horários flexíveis por dia."
        mascot={BRAND.MANGUITO}
        actions={
          <Button onClick={startCreate} data-testid="new-class-button" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] hover:bg-[hsl(var(--vv-lime)/0.85)] font-bold uppercase tracking-wider">
            <Plus className="size-4 mr-2" /> Nova turma
          </Button>
        }
      />

      {classes.length === 0 ? (
        <EmptyState title="Nenhuma turma cadastrada" description="Organize seus alunos criando turmas por categoria, dia e horário." mascot={BRAND.MANGUITA} action={<Button onClick={startCreate}>Criar turma</Button>} />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((c) => {
            const sched = deriveSchedule(c);
            return (
              <Link
                key={c.id}
                to={`/admin/turmas/${c.id}`}
                className="vv-card p-5 block hover:border-[hsl(var(--vv-lime))/0.6] transition-colors cursor-pointer group"
                data-testid={`class-card-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">{c.category}</div>
                    <h3 className="text-xl font-black group-hover:text-[hsl(var(--vv-lime))]">{c.name}</h3>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.preventDefault()}>
                    <Button size="icon" variant="ghost" onClick={() => startEdit(c)}><Pencil className="size-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(c)}><Trash2 className="size-4" /></Button>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {sched.length ? sched.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="vv-chip vv-chip-blue min-w-[42px] justify-center">{s.day}</span>
                      <span className="font-bold text-[hsl(var(--vv-yellow))]">{s.start}</span>
                      <span className="text-muted-foreground">às</span>
                      <span className="font-bold text-[hsl(var(--vv-yellow))]">{s.end}</span>
                    </div>
                  )) : <div className="text-sm text-muted-foreground">Sem horários</div>}
                </div>
                {c.description && <p className="text-sm mt-2 text-muted-foreground line-clamp-2">{c.description}</p>}
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 text-sm">
                  <Users className="size-4 text-[hsl(var(--vv-yellow))]" />
                  <span className="font-semibold">{countStudents(c.id)} aluno(s)</span>
                  <span className="ml-auto flex items-center gap-1 text-[hsl(var(--vv-lime))] font-bold uppercase text-xs tracking-widest">
                    <Eye className="size-3.5" /> Ver
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="class-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar turma" : "Nova turma"}</DialogTitle>
            <DialogDescription>Defina nome, categoria e os horários — cada dia da semana pode ter um horário diferente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Nome da turma *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="class-name-input" />
            </div>
            <div>
              <Label>Categoria *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="class-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Horários</Label>
                <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="add-schedule-row">
                  <Plus className="size-3 mr-1" /> Adicionar dia
                </Button>
              </div>
              <div className="space-y-2">
                {form.schedule.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-[110px_1fr_1fr_auto] gap-2 items-end" data-testid={`schedule-row-${idx}`}>
                    <Select value={row.day} onValueChange={(v) => updateRow(idx, { day: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                    <div>
                      <Input type="time" value={row.start} onChange={(e) => updateRow(idx, { start: e.target.value })} data-testid={`schedule-start-${idx}`} />
                    </div>
                    <div>
                      <Input type="time" value={row.end} onChange={(e) => updateRow(idx, { end: e.target.value })} data-testid={`schedule-end-${idx}`} />
                    </div>
                    <Button
                      type="button" size="icon" variant="ghost"
                      className="text-destructive"
                      onClick={() => removeRow(idx)}
                      disabled={form.schedule.length <= 1}
                      data-testid={`schedule-remove-${idx}`}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Ex: Seg 20:00 às 22:00 + Sex 21:00 às 22:00
              </div>
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-class-button">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
